import React, { useContext, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Building2, Calendar, Users, AlertTriangle, 
  Activity, FileText, Mail, Phone, User, CheckCircle, 
  XCircle, Clock, AlertCircle, Loader2 
} from 'lucide-react';
import AuthContext from '../../../../context/AuthContext';
import superAdminServices from '../../../../services/superAdminServices';

export default function OrganisationDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { authTokens } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [organizationData, setOrganizationData] = useState(null);

  useEffect(() => {
    if (id) {
      fetchOrganizationDetails();
    }
  }, [id]);

  const fetchOrganizationDetails = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await superAdminServices.getOrganizationDetails(authTokens.access, id);
      setOrganizationData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getSubscriptionStatusBadge = (subscription) => {
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-700', icon: CheckCircle, text: 'Actif' },
      expiring: { color: 'bg-yellow-100 text-yellow-700', icon: Clock, text: 'Expire bientôt' },
      expiring_soon: { color: 'bg-orange-100 text-orange-700', icon: AlertCircle, text: 'Expire très bientôt' },
      expired: { color: 'bg-red-100 text-red-700', icon: XCircle, text: 'Expiré' },
      inactive: { color: 'bg-gray-100 text-gray-700', icon: XCircle, text: 'Inactif' }
    };

    const config = statusConfig[subscription.status] || statusConfig.inactive;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${config.color}`}>
        <Icon className="h-3 w-3 mr-1" />
        {config.text}
      </span>
    );
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, color = 'blue' }) => {
    const colorClasses = {
      blue: 'bg-blue-50 text-blue-600',
      green: 'bg-green-50 text-green-600',
      yellow: 'bg-yellow-50 text-yellow-600',
      red: 'bg-red-50 text-red-600',
      purple: 'bg-purple-50 text-purple-600'
    };

    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center">
          <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
            <Icon className="h-5 w-5" />
          </div>
          <div className="ml-3">
            <p className="text-lg font-semibold text-gray-900">{value}</p>
            <p className="text-xs text-gray-600">{title}</p>
            {subtitle && <p className="text-xs text-gray-400">{subtitle}</p>}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-5 sm:p-6 max-w-7xl mx-auto w-full">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
          <span className="ml-3 text-gray-600">Chargement des détails...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-5 sm:p-6 max-w-7xl mx-auto w-full">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <span className="ml-2 text-sm text-red-700">{error}</span>
          </div>
        </div>
      </div>
    );
  }

  if (!organizationData) {
    return (
      <div className="p-5 sm:p-6 max-w-7xl mx-auto w-full">
        <div className="text-center py-12">
          <p className="text-gray-500">Aucune donnée d'organisation trouvée</p>
        </div>
      </div>
    );
  }

  const { organization, subscription, statistics, admin_users } = organizationData;

  return (
    <div className="p-5 sm:p-6 max-w-7xl mx-auto w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate('/admin-panel/organizations')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">{organization.organisation_name}</h1>
            <p className="text-xs text-gray-500">Détails de l'organisation</p>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {/* Organization Info Card */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center mb-4">
            <Building2 className="h-5 w-5 text-gray-600 mr-2" />
            <h2 className="text-sm font-semibold text-gray-900">Informations de l'organisation</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-gray-500">Nom</label>
                  <p className="text-sm text-gray-900">{organization.organisation_name}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Description</label>
                  <p className="text-sm text-gray-900">{organization.organisation_description || 'Aucune description'}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Créé par</label>
                  <p className="text-sm text-gray-900">{organization.created_by}</p>
                </div>
              </div>
            </div>
            
            <div>
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-gray-500">Statut d'abonnement</label>
                  <div className="mt-1">
                    {getSubscriptionStatusBadge(subscription)}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Date de création</label>
                  <p className="text-sm text-gray-900">
                    {new Date(organization.created_at).toLocaleDateString('fr-FR')}
                  </p>
                </div>
                {subscription.subscription_date && (
                  <div>
                    <label className="text-xs font-medium text-gray-500">Date d'abonnement</label>
                    <p className="text-sm text-gray-900">
                      {new Date(subscription.subscription_date).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                )}
                {subscription.end_subscription_date && (
                  <div>
                    <label className="text-xs font-medium text-gray-500">Fin d'abonnement</label>
                    <p className="text-sm text-gray-900">
                      {new Date(subscription.end_subscription_date).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                )}
                {subscription.days_remaining !== null && (
                  <div>
                    <label className="text-xs font-medium text-gray-500">Jours restants</label>
                    <p className="text-sm text-gray-900">{subscription.days_remaining} jours</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div>
          <h2 className="text-sm font-semibold text-gray-900 mb-4">Statistiques</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              title="Utilisateurs"
              value={statistics.users.total}
              subtitle={`${statistics.users.active} actifs`}
              icon={Users}
              color="blue"
            />
            <StatCard
              title="Risques"
              value={statistics.risks.total}
              subtitle={`${statistics.risks.pending} en attente`}
              icon={AlertTriangle}
              color="yellow"
            />
            <StatCard
              title="Activités"
              value={statistics.activities.total}
              subtitle={`${statistics.activities.active} actives`}
              icon={Activity}
              color="green"
            />
            <StatCard
              title="Processus"
              value={statistics.processes.total}
              subtitle={`${statistics.processes.published} publiés`}
              icon={FileText}
              color="purple"
            />
          </div>
        </div>

        {/* Admin Users */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <User className="h-5 w-5 text-gray-600 mr-2" />
              <h2 className="text-sm font-semibold text-gray-900">Administrateurs</h2>
            </div>
            <span className="text-xs text-gray-500">{admin_users.length} administrateur(s)</span>
          </div>

          {admin_users.length > 0 ? (
            <div className="space-y-3">
              {admin_users.map((admin) => (
                <div key={admin.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="h-8 w-8 bg-gradient-to-r from-black to-gray-900 rounded-full flex items-center justify-center">
                      <span className="text-xs font-semibold text-green-900">
                        {admin.first_name?.charAt(0) || admin.email.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {admin.first_name && admin.last_name 
                          ? `${admin.first_name} ${admin.last_name}`
                          : 'Nom non renseigné'
                        }
                      </p>
                      <div className="flex items-center space-x-4 text-xs text-gray-500">
                        <div className="flex items-center">
                          <Mail className="h-3 w-3 mr-1" />
                          {admin.email}
                        </div>
                        {admin.contact && (
                          <div className="flex items-center">
                            <Phone className="h-3 w-3 mr-1" />
                            {admin.contact}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                      admin.is_active 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {admin.is_active ? 'Actif' : 'Inactif'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <User className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">Aucun administrateur trouvé</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
